/**
 * Fetch POI data from R2 (data.sunny.coffee).
 * R2 serves JSON with Overpass-style elements plus compact tree coordinates.
 * We fetch by H3 cell, merge entries, filter by bounds, and convert to app features.
 */

import { latLngToCell } from "h3-js";
import type { LngLatBounds } from "maplibre-gl";
import type {
  BenchPointCollection,
  BenchPointFeature,
  CafePointCollection,
  CafePointFeature,
  MapDataResponse,
  OverpassElement,
  TreePointCollection,
  TreePointFeature,
} from "./types";
import { overpassElementToGeoJSONFeature, toCafePointFeature } from "./cafe-feature";

const R2_BASE = "https://data.sunny.coffee";
const R2_PREFIX = "pois/res4";
const H3_RESOLUTION = 4;
const CELL_CACHE_MAX = 4;

/** Cache last N cell fetches by H3 index (FIFO eviction). */
type TreeCoord = [number, number];
type CellData = { elements: OverpassElement[]; trees: TreeCoord[] };
const cellCache = new Map<string, CellData>();

function evictOldestCell(): void {
  if (cellCache.size < CELL_CACHE_MAX) return;
  const oldest = cellCache.keys().next();
  if (!oldest.done) cellCache.delete(oldest.value);
}

type R2OverpassResponse = {
  version?: number;
  generator?: string;
  elements: OverpassElement[];
  trees?: TreeCoord[];
};

/**
 * Get H3 cell IDs that cover the bounds. At res 4 cells are large, so polygonToCells
 * often returns [] for small viewports (no cell center inside the box). We use the
 * bounds center and corners so we always fetch the cells that cover the viewport.
 */
function boundsToCellIds(bounds: LngLatBounds): string[] {
  const center = bounds.getCenter();
  const sw = bounds.getSouthWest();
  const se = bounds.getSouthEast();
  const ne = bounds.getNorthEast();
  const nw = bounds.getNorthWest();

  const cellIds = new Set<string>();
  for (const { lat, lng } of [center, sw, se, ne, nw]) {
    cellIds.add(latLngToCell(lat, lng, H3_RESOLUTION));
  }
  return [...cellIds];
}

function toTreeId(lon: number, lat: number): number {
  const lonNorm = Math.round((lon + 180) * 1e5);
  const latNorm = Math.round((lat + 90) * 1e5);
  // Deterministic 32-bit id from coordinates for styling/feature keys.
  return (lonNorm * 1000003 + latNorm) >>> 0;
}

async function fetchCell(h3Index: string): Promise<CellData> {
  const cached = cellCache.get(h3Index);
  if (cached !== undefined) return cached;

  const url = `${R2_BASE}/${R2_PREFIX}/${h3Index}.json`;
  const response = await fetch(url, {
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    if (response.status === 404) return { elements: [], trees: [] };
    throw new Error(`R2 fetch failed: ${response.status} ${url}`);
  }

  const data = (await response.json()) as R2OverpassResponse;
  const elements = Array.isArray(data?.elements) ? data.elements : [];
  const trees = Array.isArray(data?.trees)
    ? data.trees.filter(
        (coord): coord is TreeCoord =>
          Array.isArray(coord) &&
          coord.length === 2 &&
          Number.isFinite(coord[0]) &&
          Number.isFinite(coord[1]),
      )
    : [];
  const cellData: CellData = { elements, trees };
  evictOldestCell();
  cellCache.set(h3Index, cellData);
  return cellData;
}

export async function fetchMapDataFromR2(bounds: LngLatBounds): Promise<MapDataResponse> {
  const cellIds = boundsToCellIds(bounds);
  const results = await Promise.all(cellIds.map(fetchCell));

  const seenCafeIds = new Set<number>();
  const seenBenchIds = new Set<number>();
  const seenTreeCoords = new Set<string>();
  const cafes: CafePointFeature[] = [];
  const benches: BenchPointFeature[] = [];
  const trees: TreePointFeature[] = [];
  const west = bounds.getWest();
  const east = bounds.getEast();
  const south = bounds.getSouth();
  const north = bounds.getNorth();

  for (const result of results) {
    for (const element of result.elements) {
      const osmId = element.id;
      const lat = element.lat;
      const lon = element.lon;
      if (lat == null || lon == null || lon < west || lon > east || lat < south || lat > north) {
        continue;
      }

      if (!seenCafeIds.has(osmId)) {
        const geoFeature = overpassElementToGeoJSONFeature(element);
        const feature = geoFeature ? toCafePointFeature(geoFeature) : null;
        if (feature) {
          seenCafeIds.add(osmId);
          cafes.push(feature);
          continue;
        }
      }

      const tags = element.tags ?? {};
      if (tags.amenity !== "bench" || seenBenchIds.has(osmId)) {
        continue;
      }
      seenBenchIds.add(osmId);
      benches.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [lon, lat] },
        properties: {
          osmId,
          osmType: element.type ?? "node",
          amenity: "bench",
          backrest: tags.backrest,
        },
      });
    }

    for (const [lon, lat] of result.trees) {
      if (lon < west || lon > east || lat < south || lat > north) continue;

      const coordKey = `${lon},${lat}`;
      if (seenTreeCoords.has(coordKey)) continue;
      seenTreeCoords.add(coordKey);

      trees.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [lon, lat] },
        properties: {
          osmId: toTreeId(lon, lat),
          osmType: "node",
          natural: "tree",
        },
      });
    }
  }

  const cafeCollection: CafePointCollection = {
    type: "FeatureCollection",
    features: cafes,
  };

  const treeCollection: TreePointCollection = {
    type: "FeatureCollection",
    features: trees,
  };

  const benchCollection: BenchPointCollection = {
    type: "FeatureCollection",
    features: benches,
  };

  return {
    cafes: cafeCollection,
    trees: treeCollection,
    benches: benchCollection,
  };
}
